import { useState } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { DialogTrigger } from '@radix-ui/react-dialog'

import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import { RadixModal } from '@jotta/ui/RadixModal'
import { ReactAriaCheckbox } from '@jotta/ui/ReactAriaCheckbox'
import SvgTrash from '@jotta/ui/SvgTrash'

import type { ExtendedBusinessUser } from '../BizApi'
import { bizApi } from '../BizApi'

export function BizActionButtonDeleteUser(props: {
  user: Pick<ExtendedBusinessUser, 'username' | 'name' | 'isCurrentUser'>
  className?: string
}) {
  const {
    data: { company },
  } = bizApi.company.get.useSuspenseQuery()
  return (
    <BizActionButtonDeleteUserInner
      {...props}
      companyName={company.name}
    ></BizActionButtonDeleteUserInner>
  )
}

export function BizActionButtonDeleteUserInner({
  companyName,
  user,
  className,
}: {
  user: Pick<ExtendedBusinessUser, 'username' | 'name' | 'isCurrentUser'>
  companyName: string
  className?: string
}) {
  const { t } = useLingui()
  const { username, isCurrentUser, name } = user
  const [consent, setConsent] = useState(false)
  const [open, setOpen] = useState(false)
  const onClose = (open: boolean): void => {
    if (!open) {
      setConsent(false)
    }
    setOpen(open)
  }
  const mutation = bizApi.user.remove.useMutation({
    onSettled: () => {
      onClose(false)
    },
  })
  const title = t`Remove ${name} from ${companyName}?`
  return (
    <RadixModal
      open={open}
      onClose={onClose}
      triggerElement={
        <DialogTrigger
          disabled={isCurrentUser}
          title={title}
          className={className}
          name="username"
          value={username}
        >
          <PlainBrandIcon icon={SvgTrash} />
        </DialogTrigger>
      }
    >
      <RadixDialog
        title={title}
        onClose={onClose}
        buttons={[
          {
            label: t`Delete`,
            disabled: !consent,
            action: () => mutation.mutate({ username }),
            type: 'destructive',
          },
          {
            action: 'close',
            label: t`Cancel`,
            type: 'outline',
          },
        ]}
      >
        {mutation.isPending && (
          <div className="loading loading-sm loading-absolute" />
        )}
        <div className="prose prose-small">
          <p>
            <Trans>
              This will delete the {name} user account from {companyName}.
            </Trans>
          </p>
          <p>
            <Trans>
              All files associated with this account will be deleted
            </Trans>
            .
          </p>
          <ReactAriaCheckbox isSelected={consent} onChange={setConsent}>
            <Trans>
              I understand this operation is permanent and cannot be undone
            </Trans>
          </ReactAriaCheckbox>
        </div>
      </RadixDialog>
    </RadixModal>
  )
}
